import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  alpha,
  Box,
  ListItem,
  ListItemButton,
  ListItemText,
  useTheme,
} from '@mui/material';
import Drawer, {
  CLOSED_DRAWER_GUTTER,
  OPEN_DRAWER_GUTTER,
  TRANSITION_DURATION,
} from './StyledDrawer';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  layoutEditAtom,
  searchValuesAtom,
  selectedBranchAtom,
  selectedCustomerIdAtom,
} from '../../Atoms';
import LayoutEditSidebar from '../engine/layout/LayoutEditSidebar';
import useCallHub from '../Hubs/CallHubProvider';
import { StyledList } from '../../Theme/StyledComponents';
import {
  FULLPAGE_ROUTES,
  renderRoutesTree,
  resolveRouteKey,
} from '../../Router/routes';
import { flatRoutesAtom } from '../../Router/routeAtoms';
import RouteListItem from './RouteListItem';
import SidebarSection from './SidebarSection';
import { isRouteFolder, routeItemProps } from './utils';
import { CHANNEL_TYPES } from '../../Utils/constants/general';
import FiberNewRoundedIcon from '@mui/icons-material/FiberNewRounded';
import { compareDates } from '../../Helpers/dates';
import SidebarHeader from './SidebarHeader';

const Sidebar = function Sidebar({
  handleChangeSection,
  toggleSidebar,
  open = false,
  version = '',
  routesTree = [],
}) {
  const location = useLocation().pathname;
  const navigate = useNavigate();
  const theme = useTheme();

  const newReleaseReadOn = localStorage.getItem('newReleaseReadOn');

  const setSearchValues = useSetAtom(searchValuesAtom);
  const [selectedRoute, setSelectedRoute] = useState(location);
  const [selectedBranch, setSelectedBranch] = useAtom(selectedBranchAtom);
  useCallHub(selectedBranch);
  const [newReleaseReadOnState, setNewReleaseReadOnState] = useState(
    newReleaseReadOn || null
  );
  const [selectedCustomerId, setSelectedCustomerId] = useAtom(
    selectedCustomerIdAtom
  );
  // While a layout is being arranged the sidebar is the widget menu, and it is opened to hold it:
  // the rail has no room for one, and the routes have nothing to offer until the mode ends.
  const layoutEdit = useAtomValue(layoutEditAtom);
  const editing = Boolean(layoutEdit);
  const expanded = open || editing;

  const isLongChannel = selectedBranch?.channelType === CHANNEL_TYPES.Long;
  // Union of both channels — used to detect a fullpage route at this location.
  const allRoutes = useAtomValue(flatRoutesAtom);
  const visibleRoutes = useMemo(
    () => renderRoutesTree(routesTree, isLongChannel),
    [routesTree, isLongChannel]
  );

  // Rendered in server order — ParentId, SortOrder, Id — so the sidebar shows
  // the arrangement an admin set in Route management.
  //
  // A first-level folder is a macro area and renders as a caption with a caret
  // (SidebarSection); every route under it, and every folder deeper than it,
  // renders through RouteListItem. A folder there is an ordinary item carrying
  // the folder mark. Whatever a folder holds, area or not, hangs off a guide line.
  //
  // The captions own everything up to the next one, so a route that is not in
  // an area belongs above the first caption rather than between two of them —
  // after a *closed* area it would read as its contents. That ordering is the
  // route tree's to get right, which is what Route management arranges.

  const handleListItemClick = (page) => {
    if (selectedRoute !== page) {
      handleChangeSection(page);
      setSelectedRoute(page);
    }
  };

  const handleSelectBranch = (newBranch) => {
    if (selectedCustomerId) {
      setSelectedCustomerId('');
      navigate('/'); //when here will be redirected to allClientsPage
    }
    setSelectedBranch(newBranch);
    sessionStorage.setItem('selectedBranch', JSON.stringify(newBranch));
  };

  useEffect(() => {
    setSelectedRoute(location);
  }, [location]);

  useEffect(() => {
    if (isLongChannel) {
      setSearchValues({});
      navigate('/customers');
    }
  }, [isLongChannel]);

  return (
    <Drawer
      open={expanded}
      anchor="left"
      variant="persistent"
      slotProps={{ paper: { variant: 'elevation', elevation: 3 } }}
      sx={{
        display:
          (allRoutes.find(
            (route) =>
              `/${route.target}` === location &&
              FULLPAGE_ROUTES.includes(resolveRouteKey(route))
          ) ||
            location === '/401') &&
          'none',
        // The gutter is outside the paper, so it counts against the page just as
        // the rail's own width does: collapsed, it gives that space back too.
        padding: `16px 0px 16px ${expanded ? OPEN_DRAWER_GUTTER : CLOSED_DRAWER_GUTTER}`,
        transition: `padding ${TRANSITION_DURATION}s ease-in-out`,
      }}
    >
      {/* Nothing of the application's own while a layout is being arranged: the branch is not
          changed mid-edit, and the rail cannot be collapsed while it holds the widgets. */}
      {!editing && (
        <SidebarHeader
          open={expanded}
          toggleSidebar={toggleSidebar}
          handleSelectBranch={handleSelectBranch}
        />
      )}
      {editing ? (
        <LayoutEditSidebar />
      ) : (
        <div
          className="sidebar-scroll"
          style={{ overflowY: 'auto', overflowX: 'hidden' }}
        >
          <StyledList>
            {visibleRoutes.map((route, at) =>
              // A first-level folder is a macro area: a caption over what follows rather than an
              // item to open. Anything deeper is an ordinary folder and renders as one.
              isRouteFolder(route) ? (
                <SidebarSection
                  key={route.id}
                  routeName={resolveRouteKey(route) ?? route.name}
                  routes={route.subroutes}
                  selected={selectedRoute}
                  onClick={handleListItemClick}
                  sidebarOpen={open}
                  first={at === 0}
                />
              ) : (
                <RouteListItem
                  key={route.id}
                  {...routeItemProps(route)}
                  selected={selectedRoute}
                  onClick={handleListItemClick}
                  sidebarOpen={open}
                />
              )
            )}
          </StyledList>
        </div>
      )}
      {/* Left out of the rail rather than hidden in it: at `visibility: hidden` the row was still
          laid out, and every frame of the collapse re-measured a line of text nobody can see. */}
      {!editing && version && open && (
        <ListItem
          sx={{
            marginTop: 'auto',
            padding: '8px',
          }}
          secondaryAction={
            compareDates(
              new Date(newReleaseReadOnState),
              new Date(version?.releasedOn),
              'smaller'
            ) ? (
              <Box
                sx={{
                  '@keyframes blink-new-icon': {
                    '0%': { opacity: 1 },
                    '50%': { opacity: 0 },
                  },
                  animation: 'blink-new-icon 1s infinite',
                  display: 'inline-flex',
                }}
              >
                <FiberNewRoundedIcon color="warning" />
              </Box>
            ) : null
          }
        >
          <ListItemButton
            component="a"
            href="https://docs.databeasy.it/blog"
            sx={{
              borderRadius: '8px',
              ...(compareDates(
                new Date(newReleaseReadOnState),
                new Date(version?.releasedOn),
                'smaller'
              )
                ? {
                    backgroundColor: alpha(theme.palette.warning.main, 0.1),
                    border: `2px solid ${theme.palette.warning.main}`,
                  }
                : {}),
            }}
            onClick={() => {
              localStorage.setItem('newReleaseReadOn', new Date());
              setNewReleaseReadOnState(new Date());
            }}
            target="_blank"
            disabled={
              !compareDates(
                new Date(newReleaseReadOnState),
                new Date(version?.releasedOn),
                'smaller'
              )
            }
          >
            <ListItemText
              // eslint-disable-next-line no-undef
              primary={`${ENV !== 'production' ? ENV : ''} ${version?.major}.${version?.minor}.${version?.revision}`}
              slotProps={{ primary: { sx: { textTransform: 'uppercase' } } }}
            />
          </ListItemButton>
        </ListItem>
      )}
    </Drawer>
  );
};

export default Sidebar;
